import { useEffect, useRef } from 'react';
import { DIMENSION_DISPLAY } from '@shared/types/cogniprint';

interface ParticleTarget {
  xPct: number;
  yPct: number;
}

interface ParticleFingerprintProps {
  active: boolean;
  targetPoint?: ParticleTarget | null;
  size?: number;
  canvasSize?: number;
  /** When true, particles keep jittering + pulsing around their targets after
   *  convergence instead of settling — gives a continuous "alive" / buzzing feel. */
  alive?: boolean;
  /** Normalised cursor offset (−1..1) from the orb centre. When supplied in
   *  alive mode, particles lean toward the cursor. Set to null when the cursor
   *  leaves the viewport. */
  cursorBias?: { x: number; y: number } | null;
  /** Fires a click-burst action. `key` is an incrementing counter — any change
   *  triggers the action. `count` ∈ 1..5 selects which action to play. */
  clickAction?: { key: number; count: number } | null;
}

const COLORS = DIMENSION_DISPLAY.map(d => d.colour);

export function ParticleFingerprint({
  active,
  targetPoint = null,
  size = 280,
  canvasSize = 600,
  alive = false,
  cursorBias = null,
  clickAction = null,
}: ParticleFingerprintProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef({
    active,
    targetPoint,
    alive,
    cursorBias,
    shockwaveStart: 0,
    action: null as {
      count: number;
      startTime: number;
      shockTriggered: boolean;
      spinAngle: number;
      explodeTriggered: boolean;
    } | null,
  });
  stateRef.current.active = active;
  stateRef.current.targetPoint = targetPoint;
  stateRef.current.alive = alive;
  stateRef.current.cursorBias = cursorBias;

  // Fire a click-burst action when clickAction.key changes (skip first mount).
  const clickActionMountedRef = useRef(false);
  useEffect(() => {
    if (!clickActionMountedRef.current) {
      clickActionMountedRef.current = true;
      return;
    }
    if (!clickAction) return;
    stateRef.current.action = {
      count: clickAction.count,
      startTime: performance.now(),
      shockTriggered: false,
      spinAngle: 0,
      explodeTriggered: false,
    };
    // Intentionally keyed only on clickAction.key: the burst must fire once per
    // distinct click, not whenever the clickAction object identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickAction?.key]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    const cx = canvasSize / 2;
    const cy = canvasSize / 2;

    // Generate fingerprint target positions
    const targets: Array<{
      x: number; y: number;
      ring: number; color: string;
      baseAngle: number; radius: number;
    }> = [];
    const NUM_RINGS = 14;
    for (let ring = 0; ring < NUM_RINGS; ring++) {
      const r = 35 + ring * 16;
      const startA = -Math.PI * 0.85 + ring * 0.11;
      const arcLen = 0.4 + (ring / 14) * 1.0;
      const endA = startA + Math.PI * arcLen * 1.8;
      const pts = Math.floor(r * arcLen * 0.9);
      for (let j = 0; j < pts; j++) {
        const a = startA + (endA - startA) * (j / pts);
        const w = Math.sin(j * 0.3 + ring) * 2.5;
        const effectiveR = r + w;
        targets.push({
          x: cx + effectiveR * Math.cos(a),
          y: cy + effectiveR * Math.sin(a),
          ring,
          color: COLORS[ring % 8],
          baseAngle: a,
          radius: effectiveR,
        });
      }
    }

    const particles = targets.map((t) => {
      // Partition each particle into a face part for the 2-click grin:
      //   80% mouth, 10% left eye, 10% right eye.
      const roll = Math.random();
      const facePart: 'mouth' | 'left-eye' | 'right-eye' =
        roll < 0.80 ? 'mouth' : roll < 0.90 ? 'left-eye' : 'right-eye';

      // Eye-local offset — uniform random within a disk via sqrt sampling.
      const eyeAngle = Math.random() * Math.PI * 2;
      const eyeRadiusFrac = Math.sqrt(Math.random());

      return {
        x: cx + (Math.random() - 0.5) * canvasSize * 1.6,
        y: cy + (Math.random() - 0.5) * canvasSize * 1.6,
        tx: t.x, ty: t.y, vx: 0, vy: 0,
        size: 1 + Math.random() * 1.3,
        drawSize: 1,
        color: t.color,
        alpha: 0,
        converged: false,
        // alive-mode — ring index drives the ripple (inner rings pulse first).
        ring: t.ring,
        // Intrinsic angular position + radius for ring-rotation recompute.
        baseAngle: t.baseAngle,
        radius: t.radius,
        // 2-click grin: which face part this particle belongs to and its
        // stable position within that part.
        facePart,
        smileT: Math.random(),
        smileFill: Math.random(),
        smilePerp: (Math.random() - 0.5) * 6,
        eyeAngle,
        eyeRadiusFrac,
      };
    });

    // Per-ring rotation offset (in radians) — accumulated during rotate phase.
    // Inner ring CW (+), next CCW (-), alternating by parity.
    const ringRotations: number[] = new Array(NUM_RINGS).fill(0);
    let lastFrameTime = performance.now();

    // ─── Stochastic animation state machine ──────────────────────────────
    // Instead of a fixed cycle, the orb drifts between three ambient states:
    //   'calm'   — mostly still, very faint pulse only (dominant / common)
    //   'ripple' — inner-or-outer wave sweeps through the rings
    //   'rotate' — rings counter-rotate with alternating directions
    // Durations and per-state params are randomised each entry so no two
    // instances of the same state look identical. Click-shockwaves and
    // cursor attraction still compose on top at any time.
    type AnimState = 'calm' | 'ripple' | 'rotate';
    let animState: AnimState = 'calm';
    let stateStartTime = performance.now();
    let stateDuration = 3 + Math.random() * 4; // first calm is 3-7s
    let rippleDirection: 1 | -1 = 1;            // +1 inner→outer, -1 outer→inner
    let rotateBaseSpeed = 0.75;                 // rad/sec, randomised per entry

    const pickNextState = (cur: AnimState): AnimState => {
      if (cur === 'calm') {
        return Math.random() < 0.5 ? 'ripple' : 'rotate';
      }
      // From an active state, lean back toward calm so stretches of stillness
      // are the baseline — the active states feel like occasional bursts.
      if (Math.random() < 0.65) return 'calm';
      return cur === 'ripple' ? 'rotate' : 'ripple';
    };
    const pickDuration = (s: AnimState): number => {
      switch (s) {
        case 'calm':   return 5 + Math.random() * 9;  // 5-14s (longer)
        case 'ripple': return 2.5 + Math.random() * 4; // 2.5-6.5s
        case 'rotate': return 3 + Math.random() * 5;   // 3-8s
      }
    };
    const onStateEnter = (s: AnimState) => {
      if (s === 'ripple') rippleDirection = Math.random() < 0.5 ? 1 : -1;
      if (s === 'rotate') rotateBaseSpeed = 0.45 + Math.random() * 0.6;
    };

    const startTime = performance.now();
    let rafId: number;

    const animate = (now: number) => {
      ctx.clearRect(0, 0, canvasSize, canvasSize);
      const {
        active: isActive,
        targetPoint: tp,
        alive: isAlive,
        cursorBias: bias,
        shockwaveStart,
      } = stateRef.current;
      const elapsed = now - startTime;
      const dt = Math.min(0.05, (now - lastFrameTime) / 1000); // clamp to avoid jumps
      lastFrameTime = now;

      // Alive-mode ripple — time-based wave that sweeps outward across rings.
      const globalTime = now * 0.0018;
      const ringLag = 0.5;

      // ─── Advance the random state machine ────────────────────────────
      // Skip while the initial convergence is still happening.
      if (isAlive && elapsed > 700) {
        const stateElapsed = (now - stateStartTime) / 1000;
        if (stateElapsed > stateDuration) {
          animState = pickNextState(animState);
          stateDuration = pickDuration(animState);
          stateStartTime = now;
          onStateEnter(animState);
        }
      }

      // Eased intensity for the current state — ramps in over 1s and out
      // over the last 1s so transitions between states are invisible seams.
      const fadeTime = 1.0;
      const stateElapsed = (now - stateStartTime) / 1000;
      const easeIn = Math.min(1, stateElapsed / fadeTime);
      const easeOut = Math.min(1, (stateDuration - stateElapsed) / fadeTime);
      const stateIntensity = Math.max(0, Math.min(easeIn, easeOut));

      const rippleStrength = animState === 'ripple' ? stateIntensity : 0;
      const rotateStrength = animState === 'rotate' ? stateIntensity : 0;

      // Advance each ring's rotation offset, scaled by the current state.
      if (isAlive && rotateStrength > 0) {
        for (let r = 0; r < NUM_RINGS; r++) {
          const dir = r % 2 === 0 ? 1 : -1;
          ringRotations[r] += dir * rotateBaseSpeed * rotateStrength * dt;
        }
      }

      // Shockwave state — a single expanding ring that traverses ring 0..14
      // over SHOCK_DURATION. Particles within ~2 rings of the wavefront
      // receive a strong outward push + flash.
      const SHOCK_DURATION = 1.1;                   // seconds
      const shockElapsed = (now - shockwaveStart) / 1000;
      const shockActive =
        shockwaveStart > 0 && shockElapsed >= 0 && shockElapsed < SHOCK_DURATION;
      const shockWaveRing = shockActive
        ? (shockElapsed / SHOCK_DURATION) * 15
        : -10;

      // Pre-compute cursor position in canvas coords (only used in alive mode).
      let cursorPxX = 0, cursorPxY = 0, cursorProximity = 0;
      if (bias) {
        cursorPxX = cx + bias.x * canvasSize * 0.5;
        cursorPxY = cy + bias.y * canvasSize * 0.5;
        const biasMag = Math.sqrt(bias.x * bias.x + bias.y * bias.y);
        cursorProximity = Math.max(0, 1 - biasMag / 2.2);
      }

      // ─── Click-burst action (count-based choreography) ─────────────────
      // Computes per-frame effects based on click count. Different counts get
      // different timelines.
      let actionPushForce = 0;   // radial outward push (count 1 buildup)
      let smileBlend = 0;         // 0 = normal, 1 = fully reshaped into a grin (count 2)
      let count3SpinActive = false; // override targets with a spun angle (count 3)
      let count3SpinAngle = 0;
      let count3ExplodeNow = false; // single-frame random-direction impulse (count 3)
      let heartBlend = 0;         // 0 = normal, 1 = fully reshaped into a heart (count 4)
      let heartPulseScale = 1;    // heart-beat breathing factor (count 4)
      let count5Vacuum = 0;       // 0..1 — how strongly targets are pulled to centre (count 5)
      let count5BurstNow = false; // single-frame radial starburst impulse (count 5)
      let holdActive = false;     // suspend spring so particles freeze in place
      const action = stateRef.current.action;
      if (isAlive && action) {
        const t = (now - action.startTime) / 1000;

        if (action.count === 1) {
          // "Implosion that shakes the ground": slow expansion outward, then
          // release + shockwave rebound.
          const BUILDUP_T = 0.8;
          const AFTERMATH = 1.3;
          if (t < BUILDUP_T) {
            const progress = t / BUILDUP_T;
            const eased = progress * progress * progress;
            actionPushForce = eased * 1.7;
          } else if (!action.shockTriggered) {
            stateRef.current.shockwaveStart = now;
            action.shockTriggered = true;
          }
          if (t > BUILDUP_T + AFTERMATH) stateRef.current.action = null;
        } else if (action.count === 3) {
          // "Spin out of control → explode in all directions → freeze in
          //  chaos → rebound with wobble."
          // Phase A (0 → SPIN_T): rotate all targets around the centre,
          //   angular speed ramping from 0 to MAX_SPIN (quadratic ease-in).
          // Explosion (at SPIN_T, single frame): each particle gets a
          //   velocity impulse in a COMPLETELY RANDOM direction, high mag,
          //   replacing any prior motion — shape is lost.
          // Phase B (SPIN_T → SPIN_T + HOLD_T): spring suspended. Damping
          //   quickly kills remaining velocity and particles hover in their
          //   scattered positions for the full hold duration.
          // Phase C (after): spring resumes — natural rebound + wobble.
          const SPIN_T = 3.0;
          const HOLD_T = 3.0;
          const REBOUND_T = 1.8;
          const TOTAL = SPIN_T + HOLD_T + REBOUND_T;
          const MAX_SPIN = 14; // rad/sec at end of spin-up

          if (t < SPIN_T) {
            const progress = t / SPIN_T;
            const speed = progress * progress * MAX_SPIN;
            action.spinAngle += speed * dt;
            count3SpinActive = true;
            count3SpinAngle = action.spinAngle;
          } else if (t < SPIN_T + HOLD_T) {
            holdActive = true;
          }
          if (t >= SPIN_T && !action.explodeTriggered) {
            count3ExplodeNow = true;
            action.explodeTriggered = true;
          }
          if (t > TOTAL) stateRef.current.action = null;
        } else if (action.count === 4) {
          // "Heartbeat": particles reshape into a filled heart and pulse
          // with a lub-dub rhythm for ~3 seconds, then melt back.
          const RAMP_IN = 0.5;
          const HOLD = 3.0;
          const RAMP_OUT = 0.5;
          const TOTAL = RAMP_IN + HOLD + RAMP_OUT;
          if (t < RAMP_IN) heartBlend = t / RAMP_IN;
          else if (t < RAMP_IN + HOLD) heartBlend = 1;
          else if (t < TOTAL) heartBlend = 1 - (t - RAMP_IN - HOLD) / RAMP_OUT;
          else { heartBlend = 0; stateRef.current.action = null; }
          heartBlend = 0.5 - 0.5 * Math.cos(heartBlend * Math.PI); // cosine ease

          // Lub-dub heartbeat. Each 0.85s beat cycle:
          //   0–0.10s  first beat (lub),   amplitude 0.16
          //   0.15–0.25s second beat (dub), amplitude 0.11
          //   0.25–0.85s silence
          const beatT = (t - RAMP_IN) % 0.85;
          if (beatT >= 0 && beatT < 0.10) {
            heartPulseScale = 1 + 0.16 * Math.sin((beatT / 0.10) * Math.PI);
          } else if (beatT >= 0.15 && beatT < 0.25) {
            heartPulseScale = 1 + 0.11 * Math.sin(((beatT - 0.15) / 0.10) * Math.PI);
          }
        } else if (action.count === 5) {
          // "Supernova": vacuum inward → compressed core → radial starburst
          // (each particle shoots outward along its own ring angle, creating
          // coordinated beams) → hover in beams → spring rebound with wobble.
          const VACUUM_T = 0.9;
          const COMPRESS_T = 0.25;
          const BURST_HOLD = 1.9;
          const REFORM_T = 1.6;
          const TOTAL = VACUUM_T + COMPRESS_T + BURST_HOLD + REFORM_T;

          if (t < VACUUM_T) {
            // Vacuum — strength ramps quadratically so the pull accelerates
            // as it approaches the compression moment.
            const p = t / VACUUM_T;
            count5Vacuum = p * p;
          } else if (t < VACUUM_T + COMPRESS_T) {
            count5Vacuum = 1; // hold compressed
          } else if (t < VACUUM_T + COMPRESS_T + BURST_HOLD) {
            holdActive = true; // suspend spring during the radiating-beam hover
          }
          // Fire the starburst impulse once, at the end of compression.
          if (t >= VACUUM_T + COMPRESS_T && !action.explodeTriggered) {
            count5BurstNow = true;
            action.explodeTriggered = true;
          }
          if (t > TOTAL) stateRef.current.action = null;
        } else if (action.count === 2) {
          // "Grinning wide smile" for 3 seconds: ramp in 0.5s, hold 2s,
          // ramp out 0.5s, then return control to the ambient state machine.
          const RAMP_IN = 0.5;
          const HOLD = 2.0;
          const RAMP_OUT = 0.5;
          const TOTAL = RAMP_IN + HOLD + RAMP_OUT;
          if (t < RAMP_IN) smileBlend = t / RAMP_IN;
          else if (t < RAMP_IN + HOLD) smileBlend = 1;
          else if (t < TOTAL) smileBlend = 1 - (t - RAMP_IN - HOLD) / RAMP_OUT;
          else { smileBlend = 0; stateRef.current.action = null; }
          // Smooth the blend a bit with a cosine ease so the reshape doesn't
          // feel linear.
          smileBlend = 0.5 - 0.5 * Math.cos(smileBlend * Math.PI);
        }
        // TODO: counts 3, 4, 5 — separate choreographies to come.
      }

      // Open-mouth grin geometry. Corners sit on the horizontal diameter
      // (3 o'clock and 9 o'clock), so the entire mouth lives in the lower
      // half of the orb — like a real mouth on a face.
      //   y_upper(u) = UPPER_PEAK + (CORNER_Y − UPPER_PEAK) · u²
      //   y_lower(u) = LOWER_DIP  + (CORNER_Y − LOWER_DIP)  · u²
      const SMILE_HALF_W = canvasSize * 0.45;       // corners near 3/9 o'clock
      const CORNER_Y = cy;                          // exactly on the horizontal diameter
      const UPPER_PEAK_Y = cy + canvasSize * 0.06;  // upper lip — just below the diameter
      const LOWER_DIP_Y = cy + canvasSize * 0.28;   // lower lip — dips down in the lower half

      // Eyes — two discs in the upper half of the face.
      const EYE_OFFSET_X = canvasSize * 0.18;       // horizontal distance from centre
      const EYE_CENTER_Y = cy - canvasSize * 0.16;  // vertical position above centre
      const EYE_RADIUS = canvasSize * 0.055;

      // Heart shape (classic parametric). t∈[0, 2π]:
      //   x(t) = 16 · sin³(t)
      //   y(t) = 13·cos(t) − 5·cos(2t) − 2·cos(3t) − cos(4t)
      // (y is flipped for canvas orientation so the heart points down.)
      const HEART_SCALE = canvasSize * 0.017 * heartPulseScale;  // pulsed by heartbeat
      const HEART_CENTER_Y = cy + canvasSize * 0.015;            // visual centre of heart

      for (const pt of particles) {
        if (isActive && elapsed > 700) {
          // Alive mode: recompute this particle's target from its ring's
          // current rotation offset. Non-alive mode leaves tx/ty untouched.
          if (isAlive) {
            const angle = pt.baseAngle + ringRotations[pt.ring];
            pt.tx = cx + pt.radius * Math.cos(angle);
            pt.ty = cy + pt.radius * Math.sin(angle);
          }

          // Click-burst 3: during spin-up, override target with the spun
          // angle so the whole cloud rotates around the centre.
          if (count3SpinActive) {
            const angle = pt.baseAngle + count3SpinAngle;
            pt.tx = cx + pt.radius * Math.cos(angle);
            pt.ty = cy + pt.radius * Math.sin(angle);
          }

          // Click-burst 4: blend target toward a filled heart shape.
          if (heartBlend > 0) {
            const ang = pt.smileT * 2 * Math.PI;
            const sinA = Math.sin(ang);
            const heartOutlineX = cx + HEART_SCALE * 16 * sinA * sinA * sinA;
            const heartOutlineY = HEART_CENTER_Y - HEART_SCALE *
              (13 * Math.cos(ang)
                - 5 * Math.cos(2 * ang)
                - 2 * Math.cos(3 * ang)
                - Math.cos(4 * ang));
            // Uniform-ish fill from centre to outline via sqrt sampling.
            const fillR = Math.sqrt(pt.smileFill);
            const heartX = cx + (heartOutlineX - cx) * fillR;
            const heartY = HEART_CENTER_Y + (heartOutlineY - HEART_CENTER_Y) * fillR;
            pt.tx = pt.tx + (heartX - pt.tx) * heartBlend;
            pt.ty = pt.ty + (heartY - pt.ty) * heartBlend;
          }

          // Click-burst 5: vacuum — blend target toward the orb centre.
          if (count5Vacuum > 0) {
            pt.tx = pt.tx + (cx - pt.tx) * count5Vacuum;
            pt.ty = pt.ty + (cy - pt.ty) * count5Vacuum;
          }

          // Click-burst 5: radial starburst impulse. Direction = particle's
          // own ring angle so particles stream outward as coordinated beams
          // (the counterpoint to count 3's random-direction chaos).
          if (count5BurstNow) {
            const a = pt.baseAngle + (Math.random() - 0.5) * 0.15; // tiny jitter
            const speed = 22 + Math.random() * 10; // 22–32 px/frame
            pt.vx = Math.cos(a) * speed;
            pt.vy = Math.sin(a) * speed;
          }

          // Click-burst 3: chaotic explosion. Each particle gets a velocity
          // in a COMPLETELY RANDOM direction (not radial), REPLACING prior
          // motion so the spin's rotation is wiped — shape is lost.
          // Magnitude tuned so particles scatter widely but remain inside
          // the (now circle-clipped) canvas so the chaos stays visible.
          if (count3ExplodeNow) {
            const a = Math.random() * Math.PI * 2;
            const speed = 14 + Math.random() * 20; // 14–34 px/frame
            pt.vx = Math.cos(a) * speed;
            pt.vy = Math.sin(a) * speed;
          }

          // Click-burst 2: blend target toward the grin (mouth + two eyes).
          if (smileBlend > 0) {
            let faceX = 0, faceY = 0;
            if (pt.facePart === 'mouth') {
              const u = (pt.smileT - 0.5) * 2;        // -1..1
              const uSq = u * u;
              const yUpper = UPPER_PEAK_Y + (CORNER_Y - UPPER_PEAK_Y) * uSq;
              const yLower = LOWER_DIP_Y + (CORNER_Y - LOWER_DIP_Y) * uSq;
              faceX = cx + u * SMILE_HALF_W + pt.smilePerp * 0.4;
              faceY = yUpper + (yLower - yUpper) * pt.smileFill;
            } else {
              // Eye — uniform random point within a disc.
              const eyeCx = pt.facePart === 'left-eye'
                ? cx - EYE_OFFSET_X
                : cx + EYE_OFFSET_X;
              const r = pt.eyeRadiusFrac * EYE_RADIUS;
              faceX = eyeCx + r * Math.cos(pt.eyeAngle);
              faceY = EYE_CENTER_Y + r * Math.sin(pt.eyeAngle);
            }
            pt.tx = pt.tx + (faceX - pt.tx) * smileBlend;
            pt.ty = pt.ty + (faceY - pt.ty) * smileBlend;
          }

          // Click-burst 1: outward radial push during buildup.
          if (actionPushForce > 0) {
            const rdx = pt.x - cx;
            const rdy = pt.y - cy;
            const rd = Math.max(1, Math.sqrt(rdx * rdx + rdy * rdy));
            pt.vx += (rdx / rd) * actionPushForce;
            pt.vy += (rdy / rd) * actionPushForce;
          }

          const dx = pt.tx - pt.x;
          const dy = pt.ty - pt.y;

          if (isAlive) {
            // Ring-synchronised pulse — direction randomised each ripple entry
            // (sometimes inner→outer, sometimes outer→inner).
            const phase = globalTime - pt.ring * ringLag * rippleDirection;
            const raw = Math.sin(phase);
            const basePulse = raw > 0 ? raw * raw : 0;
            // In calm state rippleStrength=0, but we keep a small baseline so
            // there's always a *very faint* pulse — never fully dead.
            const pulseAmount = 0.06 + rippleStrength * 0.94;
            const pulse = basePulse * pulseAmount;

            // Shudder scales with pulse — near zero in calm, strong in ripple.
            const shudder = 0.04 + pulse * 1.9;
            pt.vx += (Math.random() - 0.5) * shudder;
            pt.vy += (Math.random() - 0.5) * shudder;

            // Alpha — stable dim around 0.45 in calm, bright peaks in ripple.
            const targetAlpha = 0.42 + pulse * 0.48;
            pt.alpha += (targetAlpha - pt.alpha) * 0.18;

            // Size bloom only meaningful when pulse is significant.
            pt.drawSize = pt.size * (1 + pulse * 0.65);

            // Cursor bias — small attractive pull toward the cursor, scaled
            // by cursor proximity to the orb centre so the effect fades out
            // when the mouse moves away.
            if (cursorProximity > 0) {
              const pdx = cursorPxX - pt.x;
              const pdy = cursorPxY - pt.y;
              const pd = Math.max(1, Math.sqrt(pdx * pdx + pdy * pdy));
              const pull = cursorProximity * 0.55;
              pt.vx += (pdx / pd) * pull;
              pt.vy += (pdy / pd) * pull;
            }

            // Shockwave — particles within ~2 rings of the wavefront get a
            // strong radial outward push + brightness spike + size bloom.
            if (shockActive) {
              const ringDiff = Math.abs(pt.ring - shockWaveRing);
              if (ringDiff < 2) {
                const intensity = 1 - ringDiff / 2;
                const rdx = pt.x - cx;
                const rdy = pt.y - cy;
                const rd = Math.max(1, Math.sqrt(rdx * rdx + rdy * rdy));
                const push = intensity * 4.5;
                pt.vx += (rdx / rd) * push;
                pt.vy += (rdy / rd) * push;
                pt.alpha = Math.min(1, pt.alpha + intensity * 0.6);
                pt.drawSize = Math.max(pt.drawSize, pt.size * (1 + intensity * 2.4));
              }
            }
          } else {
            pt.alpha = Math.min(0.88, pt.alpha + 0.022);
            pt.drawSize = pt.size;
          }

          if (holdActive) {
            // Count 3 hold — suspend spring attraction so exploded particles
            // stay in their chaotic scattered positions. Pure damping only.
            pt.vx *= 0.88;
            pt.vy *= 0.88;
          } else {
            pt.vx = (pt.vx + dx * 0.04) * 0.88;
            pt.vy = (pt.vy + dy * 0.04) * 0.88;
          }
          pt.x += pt.vx;
          pt.y += pt.vy;
          if (Math.abs(dx) < 2 && Math.abs(dy) < 2) pt.converged = true;
        } else if (isActive) {
          pt.x += (Math.random() - 0.5) * 2.5;
          pt.y += (Math.random() - 0.5) * 2.5;
          pt.alpha = Math.min(0.18, pt.alpha + 0.006);
          pt.drawSize = pt.size;
        } else if (tp) {
          const vacX = canvasSize * (tp.xPct || 0.5);
          const vacY = canvasSize * (tp.yPct || 1.0);
          const dx = vacX - pt.x;
          const dy = vacY - pt.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const speed = 0.06 + (1 - dist / canvasSize) * 0.04;
          pt.vx = (pt.vx + dx * speed) * 0.85;
          pt.vy = (pt.vy + dy * speed) * 0.85;
          pt.x += pt.vx;
          pt.y += pt.vy;
          pt.alpha = Math.max(0, pt.alpha - 0.015);
          pt.converged = false;
          pt.size = Math.max(0.3, pt.size * 0.997);
          pt.drawSize = pt.size;
        } else {
          pt.alpha = Math.max(0, pt.alpha - 0.03);
          pt.drawSize = pt.size;
        }

        if (pt.alpha > 0.01) {
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, pt.drawSize, 0, Math.PI * 2);
          ctx.fillStyle = pt.color + Math.floor(pt.alpha * 255).toString(16).padStart(2, '0');
          ctx.fill();
          if (pt.converged && isActive) {
            ctx.beginPath();
            ctx.arc(pt.x, pt.y, pt.drawSize * 3, 0, Math.PI * 2);
            ctx.fillStyle = pt.color + '0e';
            ctx.fill();
          }
        }
      }

      // Center glow after convergence
      if (isActive && elapsed > 2800) {
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, 35);
        g.addColorStop(0, '#F0A50048');
        g.addColorStop(1, '#F0A50000');
        ctx.fillStyle = g;
        ctx.fillRect(cx - 40, cy - 40, 80, 80);
        ctx.beginPath();
        ctx.arc(cx, cy, 5, 0, Math.PI * 2);
        ctx.fillStyle = '#F0A500';
        ctx.fill();
      }

      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [canvasSize]);

  return (
    <canvas
      ref={canvasRef}
      className="block pointer-events-none transition-opacity duration-500"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        opacity: active || stateRef.current.targetPoint ? 1 : 0,
      }}
    />
  );
}
